import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    Dialog, DialogContent, DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    Plus, Loader2, Send, MousePointerClick, BarChart3,
    Megaphone, Calendar, Repeat,
    Zap, Ban, RotateCcw, Eye, Bell, ExternalLink,
    LayoutGrid, List, Copy, AlertTriangle, Upload,
} from 'lucide-react';
import {
    getPushDomains, getPushCampaigns, cancelPushCampaign, resendPushCampaign, getPushGroups,
} from '@/services/api/push';
import type { PushCampaign, PushNotificationGroup } from '@/types/push';

// Helper: cron → texto legível
function cronToText(cron: string | null): string {
    if (!cron) return '-';
    const parts = cron.split(' ');
    if (parts.length !== 5) return cron;

    const [min, hour, dom, , dow] = parts;

    const dayNames: Record<string, string> = {
        '0': 'Dom', '1': 'Seg', '2': 'Ter', '3': 'Qua',
        '4': 'Qui', '5': 'Sex', '6': 'Sáb', '7': 'Dom',
    };

    const formatTime = (h: string, m: string) => {
        return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
    };

    // A cada X minutos (teste)
    if (min.startsWith('*/')) {
        return `A cada ${min.replace('*/', '')} min`;
    }

    // A cada X horas
    if (hour.startsWith('*/') && dom === '*') {
        return `A cada ${hour.replace('*/', '')}h`;
    }

    // Dias específicos do mês
    if (dom !== '*' && dow === '*') {
        const days = dom.split(',').join(' e ');
        return `Dia ${days} às ${formatTime(hour, min)}`;
    }

    // Dias da semana
    if (dow !== '*') {
        let dayList: string[] = [];

        if (dow.includes('-')) {
            const [start, end] = dow.split('-');
            const s = parseInt(start), e = parseInt(end);
            for (let i = s; i <= e; i++) dayList.push(dayNames[String(i)] || String(i));
        } else if (dow.includes(',')) {
            dayList = dow.split(',').map(d => dayNames[d.trim()] || d.trim());
        } else {
            dayList = [dayNames[dow] || dow];
        }

        if (dayList.length === 5 && !dayList.includes('Sáb') && !dayList.includes('Dom')) {
            return `Seg a Sex às ${formatTime(hour, min)}`;
        }

        return `${dayList.join(', ')} às ${formatTime(hour, min)}`;
    }

    // Todo dia
    if (dom === '*' && dow === '*') {
        return `Todo dia às ${formatTime(hour, min)}`;
    }

    return cron;
}

// Helper: format date
function formatDate(d: string | null): string {
    if (!d) return '-';
    return new Date(d).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

// Helper: format number
function formatNum(n: number): string {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
}

const statusConfig: Record<string, { label: string; dot: string; bg: string }> = {
    draft: { label: 'Rascunho', dot: 'bg-gray-400', bg: 'bg-gray-50 text-gray-600 border-gray-200' },
    scheduled: { label: 'Agendada', dot: 'bg-blue-500', bg: 'bg-blue-50 text-blue-700 border-blue-200' },
    sending: { label: 'Enviando', dot: 'bg-amber-500', bg: 'bg-amber-50 text-amber-700 border-amber-200' },
    sent: { label: 'Enviada', dot: 'bg-emerald-500', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    cancelled: { label: 'Cancelada', dot: 'bg-red-400', bg: 'bg-red-50 text-red-600 border-red-200' },
    failed: { label: 'Falhou', dot: 'bg-red-500', bg: 'bg-red-50 text-red-600 border-red-200' },
    active: { label: 'Ativa', dot: 'bg-emerald-500', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    paused: { label: 'Pausada', dot: 'bg-amber-500', bg: 'bg-amber-50 text-amber-600 border-amber-200' },
};

const typeConfig: Record<string, { label: string; icon: typeof Zap; color: string }> = {
    direct: { label: 'Direto', icon: Zap, color: 'text-blue-600' },
    scheduled: { label: 'Agendado', icon: Calendar, color: 'text-purple-600' },
    recurring: { label: 'Recorrente', icon: Repeat, color: 'text-emerald-600' },
};



export default function Campaigns() {
    const [selectedDomainId, setSelectedDomainId] = useState<number | undefined>(undefined);
    const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
    const [previewCampaign, setPreviewCampaign] = useState<PushCampaign | null>(null);
    const [resendConfirmCampaign, setResendConfirmCampaign] = useState<PushCampaign | null>(null);

    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: domains = [] } = useQuery({ queryKey: ['push-domains'], queryFn: getPushDomains });
    const configuredDomains = domains.filter((d) => d.push_config?.mongo_domain_id);
    const { data: groups = [] } = useQuery<PushNotificationGroup[]>({ queryKey: ['push-groups'], queryFn: getPushGroups });

    const { data: campaignsData, isLoading } = useQuery({
        queryKey: ['push-campaigns', selectedDomainId, selectedGroupId, statusFilter, typeFilter, page],
        queryFn: () => getPushCampaigns({
            domain_id: selectedDomainId,
            group_id: selectedGroupId,
            status: statusFilter || undefined,
            type: typeFilter || undefined,
            page,
            limit: 20,
        }),
    });

    const cancelMutation = useMutation({
        mutationFn: cancelPushCampaign,
        onSuccess: () => {
            toast({ title: 'Campanha cancelada' });
            queryClient.invalidateQueries({ queryKey: ['push-campaigns'] });
        },
    });

    const resendMutation = useMutation({
        mutationFn: resendPushCampaign,
        onSuccess: () => {
            toast({ title: 'Campanha reenviada!' });
            queryClient.invalidateQueries({ queryKey: ['push-campaigns'] });
        },
    });

    const campaigns = campaignsData?.items || [];
    const pagination = campaignsData;

    // Stats agregados
    const aggregatedStats = useMemo(() => {
        const all = campaigns;
        const totalCampaigns = pagination?.total || 0;
        const totalSent = all.reduce((s, c) => s + c.total_sent, 0);
        const totalClicks = all.reduce((s, c) => s + c.total_clicks, 0);
        const avgCtr = totalSent > 0 ? (totalClicks / totalSent) * 100 : 0;
        return { totalCampaigns, totalSent, totalClicks, avgCtr };
    }, [campaigns, pagination]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Campanhas</h1>
                    <p className="mt-1 text-gray-500">Crie e gerencie suas campanhas de push notification</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/push/campaigns/batch')}
                        className="rounded-xl gap-2"
                    >
                        <Upload className="h-4 w-4" /> Importar em Lote
                    </Button>
                    <Button
                        onClick={() => navigate('/push/campaigns/new')}
                        className="rounded-xl bg-gray-900 hover:bg-gray-800 text-white gap-2"
                    >
                        <Plus className="h-4 w-4" /> Nova Campanha
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                    { label: 'Campanhas', value: aggregatedStats.totalCampaigns, icon: Megaphone, bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-900' },
                    { label: 'Enviados', value: aggregatedStats.totalSent, icon: Send, bg: 'bg-blue-50/70', border: 'border-blue-200', text: 'text-blue-700' },
                    { label: 'Cliques', value: aggregatedStats.totalClicks, icon: MousePointerClick, bg: 'bg-emerald-50/70', border: 'border-emerald-200', text: 'text-emerald-700' },
                    { label: 'CTR Médio', value: aggregatedStats.avgCtr, icon: BarChart3, bg: 'bg-purple-50/60', border: 'border-purple-200', text: 'text-purple-700', suffix: '%' },
                ].map((s) => {
                    const Icon = s.icon;
                    return (
                        <div key={s.label} className={`rounded-2xl border ${s.border} ${s.bg} p-4 backdrop-blur-sm`}>
                            <div className="flex items-center justify-between">
                                <Icon className="h-4 w-4 text-gray-400" />
                            </div>
                            <p className={`mt-2 text-2xl font-bold ${s.text}`}>
                                {s.suffix ? s.value.toFixed(1) + s.suffix : formatNum(s.value)}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-400 uppercase tracking-wider">{s.label}</p>
                        </div>
                    );
                })}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <Select value={selectedDomainId?.toString() || 'all'} onValueChange={(v) => { setSelectedDomainId(v === 'all' ? undefined : parseInt(v)); setSelectedGroupId(undefined); setPage(1); }}>
                    <SelectTrigger className="w-56 rounded-xl border-gray-200 bg-white shadow-sm"><SelectValue placeholder="Todos os dominios" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos os dominios</SelectItem>
                        {configuredDomains.map((d) => (<SelectItem key={d.id} value={d.id.toString()}>{d.domain}</SelectItem>))}
                    </SelectContent>
                </Select>

                <Select value={selectedGroupId?.toString() || 'all'} onValueChange={(v) => { setSelectedGroupId(v === 'all' ? undefined : parseInt(v)); setSelectedDomainId(undefined); setPage(1); }}>
                    <SelectTrigger className="w-48 rounded-xl border-gray-200 bg-white shadow-sm"><SelectValue placeholder="Todos os grupos" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos os grupos</SelectItem>
                        {groups.map((g) => (<SelectItem key={g.id} value={g.id.toString()}>{g.name}</SelectItem>))}
                    </SelectContent>
                </Select>

                <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="w-40 rounded-xl border-gray-200 bg-white shadow-sm"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="draft">Rascunho</SelectItem>
                        <SelectItem value="scheduled">Agendada</SelectItem>
                        <SelectItem value="sending">Enviando</SelectItem>
                        <SelectItem value="sent">Enviada</SelectItem>
                        <SelectItem value="cancelled">Cancelada</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={typeFilter || 'all'} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="w-40 rounded-xl border-gray-200 bg-white shadow-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="direct">Direto</SelectItem>
                        <SelectItem value="scheduled">Agendado</SelectItem>
                        <SelectItem value="recurring">Recorrente</SelectItem>
                    </SelectContent>
                </Select>

                <div className="ml-auto flex items-center rounded-xl border border-gray-200 bg-white p-1">
                    <button onClick={() => setViewMode('table')} className={`flex items-center justify-center rounded-lg p-1.5 transition-colors ${viewMode === 'table' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-600'}`}><List className="h-4 w-4" /></button>
                    <button onClick={() => setViewMode('cards')} className={`flex items-center justify-center rounded-lg p-1.5 transition-colors ${viewMode === 'cards' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-600'}`}><LayoutGrid className="h-4 w-4" /></button>
                </div>
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="flex items-center justify-center rounded-2xl border bg-white/60 p-16 backdrop-blur-sm">
                    <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
                </div>
            )}

            {/* Empty */}
            {!isLoading && campaigns.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/60 p-16 backdrop-blur-sm">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                        <Megaphone className="h-7 w-7 text-gray-400" />
                    </div>
                    <h3 className="mt-5 text-base font-semibold text-gray-900">Nenhuma campanha encontrada</h3>
                    <p className="mt-1.5 text-sm text-gray-400">Crie sua primeira campanha para começar a engajar seus leads.</p>
                    <Button onClick={() => navigate('/push/campaigns/new')} className="mt-4 rounded-xl bg-gray-900 hover:bg-gray-800 text-white gap-2">
                        <Plus className="h-4 w-4" /> Criar campanha
                    </Button>
                </div>
            )}

            {/* Campaigns Table */}
            {!isLoading && campaigns.length > 0 && viewMode === 'table' && (
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <table className="w-full text-sm">
                        <thead>
                        <tr className="border-b bg-gray-50/80">
                            <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Campanha</th>
                            <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Tipo</th>
                            <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Status</th>
                            <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Envios</th>
                            <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Cliques</th>
                            <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">CTR</th>
                            <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Data</th>
                            <th className="px-5 py-3.5 w-10"></th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {campaigns.map((campaign) => {
                            const st = statusConfig[campaign.status] || statusConfig.draft;
                            const tp = typeConfig[campaign.type] || typeConfig.direct;
                            const TypeIcon = tp.icon;
                            return (
                                <tr key={campaign.id} className="cursor-pointer transition-colors hover:bg-gray-50/60" onClick={() => setPreviewCampaign(campaign)}>
                                    <td className="px-5 py-3.5">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100"><Bell className="h-4 w-4 text-gray-500" /></div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-gray-900 truncate max-w-[200px]">{campaign.name}</p>
                                                <p className="text-xs text-gray-400 truncate max-w-[200px]">{campaign.title}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-3.5">
                                        <div className="flex items-center gap-1.5"><TypeIcon className={`h-3.5 w-3.5 ${tp.color}`} /><span className="text-xs text-gray-600">{tp.label}</span></div>
                                        {campaign.type === 'recurring' && campaign.recurring_cron && (<p className="mt-0.5 text-[11px] text-gray-400">{cronToText(campaign.recurring_cron)}</p>)}
                                        {campaign.type === 'scheduled' && campaign.scheduled_at && (<p className="mt-0.5 text-[11px] text-gray-400">{formatDate(campaign.scheduled_at)}</p>)}
                                    </td>
                                    <td className="px-5 py-3.5">
                                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${st.bg}`}><span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}</span>
                                    </td>
                                    <td className="px-5 py-3.5">
                                        <p className="font-medium text-gray-900">{formatNum(campaign.total_sent)}</p>
                                        {campaign.total_failed > 0 && <p className="text-[11px] text-red-400">{formatNum(campaign.total_failed)} falharam</p>}
                                    </td>
                                    <td className="px-5 py-3.5"><p className="font-medium text-gray-900">{formatNum(campaign.total_clicks)}</p></td>
                                    <td className="px-5 py-3.5">
                                        <div className="flex items-center gap-2">
                                            <div className="h-1.5 w-16 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(campaign.ctr, 100)}%` }} /></div>
                                            <span className="text-xs font-medium text-gray-600">{campaign.ctr.toFixed(1)}%</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-3.5 text-xs text-gray-400">{formatDate(campaign.sent_at || campaign.scheduled_at || campaign.created_at)}</td>
                                    <td className="px-5 py-3.5">
                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            {(campaign.status === 'scheduled' || campaign.status === 'sending') && (<button onClick={() => { if (confirm('Cancelar campanha?')) cancelMutation.mutate(campaign.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors" title="Cancelar"><Ban className="h-4 w-4" /></button>)}
                                            {campaign.status === 'sent' && (<button onClick={() => setResendConfirmCampaign(campaign)} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-500 transition-colors" title="Reenviar"><RotateCcw className="h-4 w-4" /></button>)}
                                            <button onClick={() => navigate('/push/campaigns/new', { state: { duplicate: campaign } })} className="p-1.5 rounded-lg hover:bg-purple-50 text-gray-400 hover:text-purple-500 transition-colors" title="Duplicar"><Copy className="h-4 w-4" /></button>
                                            <button onClick={() => setPreviewCampaign(campaign)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Preview"><Eye className="h-4 w-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-3">
                    <Button variant="outline" size="sm" className="rounded-xl" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                    <span className="text-sm text-gray-400">{page} de {pagination.pages}</span>
                    <Button variant="outline" size="sm" className="rounded-xl" disabled={page >= pagination.pages} onClick={() => setPage(page + 1)}>Próxima</Button>
                </div>
            )}

            {/* === CARDS VIEW === */}
            {!isLoading && campaigns.length > 0 && viewMode === 'cards' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {campaigns.map((campaign) => {
                        const st = statusConfig[campaign.status] || statusConfig.draft;
                        const tp = typeConfig[campaign.type] || typeConfig.direct;
                        const TypeIcon = tp.icon;
                        return (
                            <div key={campaign.id} onClick={() => setPreviewCampaign(campaign)} className="group cursor-pointer rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-gray-300 transition-all overflow-hidden">
                                {campaign.image ? (
                                    <div className="relative h-32 overflow-hidden">
                                        <img src={campaign.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }} />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium backdrop-blur-sm ${st.bg}`}><span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}</span>
                                            <div className="flex items-center gap-1 text-[11px] font-medium text-white/90"><TypeIcon className="h-3 w-3" /> {tp.label}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative h-20 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                                        <Bell className="h-8 w-8 text-gray-200" />
                                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${st.bg}`}><span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}</span>
                                            <div className={`flex items-center gap-1 text-[11px] font-medium ${tp.color}`}><TypeIcon className="h-3 w-3" /> {tp.label}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="p-4 space-y-3">
                                    <div>
                                        <p className="font-semibold text-gray-900 truncate">{campaign.name}</p>
                                        <p className="mt-0.5 text-xs text-gray-400 truncate">{campaign.title}</p>
                                    </div>
                                    {campaign.type === 'recurring' && campaign.recurring_cron && (
                                        <div className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1.5"><Repeat className="h-3.5 w-3.5 text-emerald-600" /><span className="text-xs font-medium text-emerald-700">{cronToText(campaign.recurring_cron)}</span></div>
                                    )}
                                    {campaign.type === 'scheduled' && campaign.scheduled_at && (
                                        <div className="flex items-center gap-1.5 rounded-lg bg-purple-50 px-2.5 py-1.5"><Calendar className="h-3.5 w-3.5 text-purple-600" /><span className="text-xs font-medium text-purple-700">{formatDate(campaign.scheduled_at)}</span></div>
                                    )}
                                    <div className="grid grid-cols-3 gap-2">
                                        <div className="rounded-lg bg-gray-50 p-2 text-center"><p className="text-sm font-bold text-gray-900">{formatNum(campaign.total_sent)}</p><p className="text-[10px] text-gray-400 uppercase">Envios</p></div>
                                        <div className="rounded-lg bg-gray-50 p-2 text-center"><p className="text-sm font-bold text-gray-900">{formatNum(campaign.total_clicks)}</p><p className="text-[10px] text-gray-400 uppercase">Cliques</p></div>
                                        <div className="rounded-lg bg-gray-50 p-2 text-center"><p className="text-sm font-bold text-emerald-700">{campaign.ctr.toFixed(1)}%</p><p className="text-[10px] text-gray-400 uppercase">CTR</p></div>
                                    </div>
                                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                        <span className="text-[11px] text-gray-400">{formatDate(campaign.sent_at || campaign.scheduled_at || campaign.created_at)}</span>
                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            {(campaign.status === 'scheduled' || campaign.status === 'sending') && (<button onClick={() => { if (confirm('Cancelar?')) cancelMutation.mutate(campaign.id); }} className="p-1 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"><Ban className="h-3.5 w-3.5" /></button>)}
                                            {campaign.status === 'sent' && (<button onClick={() => setResendConfirmCampaign(campaign)} className="p-1 rounded-md hover:bg-blue-50 text-gray-400 hover:text-blue-500 transition-colors" title="Reenviar"><RotateCcw className="h-3.5 w-3.5" /></button>)}
                                            <button onClick={() => navigate('/push/campaigns/new', { state: { duplicate: campaign } })} className="p-1 rounded-md hover:bg-purple-50 text-gray-400 hover:text-purple-500 transition-colors" title="Duplicar"><Copy className="h-3.5 w-3.5" /></button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* === RESEND CONFIRMATION DIALOG === */}
            <Dialog open={!!resendConfirmCampaign} onOpenChange={(open) => { if (!open) setResendConfirmCampaign(null); }}>
                <DialogContent className="sm:max-w-md rounded-2xl">
                    <div className="flex flex-col items-center text-center p-4">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 mb-4">
                            <AlertTriangle className="h-7 w-7 text-amber-500" />
                        </div>
                        <DialogTitle className="text-lg font-bold text-gray-900">Reenviar campanha?</DialogTitle>
                        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                            Essa ação irá reenviar a campanha <strong className="text-gray-700">{resendConfirmCampaign?.name}</strong> para todos os leads elegíveis. As métricas anteriores serão zeradas.
                        </p>
                        <div className="mt-6 flex gap-3 w-full">
                            <Button
                                variant="outline"
                                className="flex-1 rounded-xl"
                                onClick={() => setResendConfirmCampaign(null)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                className="flex-1 rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
                                onClick={() => {
                                    if (resendConfirmCampaign) {
                                        resendMutation.mutate(resendConfirmCampaign.id);
                                        setResendConfirmCampaign(null);
                                    }
                                }}
                                disabled={resendMutation.isPending}
                            >
                                {resendMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                <RotateCcw className="mr-2 h-4 w-4" />
                                Confirmar Reenvio
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* === PREVIEW DIALOG === */}
            <Dialog open={!!previewCampaign} onOpenChange={(open) => { if (!open) setPreviewCampaign(null); }}>
                <DialogContent className="sm:max-w-lg p-0 overflow-hidden rounded-2xl gap-0">
                    {previewCampaign && (() => {
                        const st = statusConfig[previewCampaign.status] || statusConfig.draft;
                        const tp = typeConfig[previewCampaign.type] || typeConfig.direct;
                        const TypeIcon = tp.icon;
                        const domainName = domains.find(d => d.id === previewCampaign.domain_id)?.domain || '';
                        return (
                            <div className="flex flex-col max-h-[85vh]">
                                {/* Header visual */}
                                <div className="relative">
                                    {previewCampaign.image ? (
                                        <div className="h-44 overflow-hidden">
                                            <img src={previewCampaign.image} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).parentElement!.classList.add('bg-gradient-to-br', 'from-gray-100', 'to-gray-200'); (e.target as HTMLImageElement).style.display = 'none'; }} />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                        </div>
                                    ) : (
                                        <div className="h-28 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center">
                                            <Bell className="h-10 w-10 text-gray-200" />
                                        </div>
                                    )}
                                    <div className={`absolute bottom-0 left-0 right-0 p-5 ${previewCampaign.image ? 'text-white' : 'text-gray-900'}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${previewCampaign.image ? 'bg-white/20 backdrop-blur-md text-white border-white/30' : st.bg}`}>
                                                <span className={`h-1.5 w-1.5 rounded-full ${previewCampaign.image ? 'bg-white' : st.dot}`} />{st.label}
                                            </span>
                                            <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${previewCampaign.image ? 'text-white/80' : tp.color}`}>
                                                <TypeIcon className="h-3 w-3" /> {tp.label}
                                            </span>
                                        </div>
                                        <DialogTitle className={`text-lg font-bold leading-tight ${previewCampaign.image ? 'text-white' : 'text-gray-900'}`}>{previewCampaign.name}</DialogTitle>
                                        {domainName && <p className={`mt-0.5 text-xs ${previewCampaign.image ? 'text-white/60' : 'text-gray-400'}`}>{domainName}</p>}
                                    </div>
                                </div>

                                {/* Scrollable content */}
                                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                                    {/* Notification preview card */}
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Preview da notificação</p>
                                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4">
                                            <div className="flex gap-3">
                                                {previewCampaign.icon ? (
                                                    <img src={previewCampaign.icon} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0 border border-gray-100" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                ) : (
                                                    <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center shrink-0"><Bell className="h-5 w-5 text-gray-300" /></div>
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-bold text-gray-900">{previewCampaign.title}</p>
                                                    <p className="mt-1 text-[13px] text-gray-600 leading-relaxed">{previewCampaign.body}</p>
                                                    {previewCampaign.url && (
                                                        <a href={previewCampaign.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-600 transition-colors">
                                                            <ExternalLink className="h-3 w-3" /><span className="truncate max-w-[250px]">{previewCampaign.url}</span>
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Stats */}
                                    {(previewCampaign.total_sent > 0 || previewCampaign.status !== 'draft') && (
                                        <div className="grid grid-cols-4 gap-2">
                                            {[
                                                { icon: Send, value: previewCampaign.total_sent, label: 'Enviados', color: 'text-gray-900' },
                                                { icon: Eye, value: previewCampaign.total_delivered, label: 'Entregues', color: 'text-gray-900' },
                                                { icon: MousePointerClick, value: previewCampaign.total_clicks, label: 'Cliques', color: 'text-emerald-700' },
                                                { icon: BarChart3, value: previewCampaign.ctr, label: 'CTR', color: 'text-purple-700', isPercent: true },
                                            ].map((item) => {
                                                const Icon = item.icon;
                                                return (
                                                    <div key={item.label} className="rounded-xl border border-gray-100 bg-gray-50/70 p-2.5 text-center">
                                                        <Icon className="h-3.5 w-3.5 text-gray-400 mx-auto" />
                                                        <p className={`mt-1 text-base font-bold ${item.color}`}>
                                                            {item.isPercent ? `${(item.value as number).toFixed(1)}%` : formatNum(item.value as number)}
                                                        </p>
                                                        <p className="text-[9px] text-gray-400 uppercase tracking-wider">{item.label}</p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Schedule info */}
                                    {previewCampaign.type === 'recurring' && previewCampaign.recurring_cron && (
                                        <div className="flex items-center gap-2.5 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                                            <Repeat className="h-4 w-4 text-emerald-600 shrink-0" />
                                            <div>
                                                <p className="text-xs font-semibold text-emerald-800">{cronToText(previewCampaign.recurring_cron)}</p>
                                                {previewCampaign.recurring_until && <p className="text-[11px] text-emerald-600">Até {formatDate(previewCampaign.recurring_until)}</p>}
                                            </div>
                                        </div>
                                    )}
                                    {previewCampaign.type === 'scheduled' && previewCampaign.scheduled_at && (
                                        <div className="flex items-center gap-2.5 rounded-xl bg-purple-50 border border-purple-100 px-4 py-3">
                                            <Calendar className="h-4 w-4 text-purple-600 shrink-0" />
                                            <p className="text-xs font-semibold text-purple-800">{formatDate(previewCampaign.scheduled_at)}</p>
                                        </div>
                                    )}

                                    {/* Details */}
                                    <div className="space-y-2">
                                        {[
                                            { label: 'Criada em', value: formatDate(previewCampaign.created_at) },
                                            previewCampaign.sent_at ? { label: 'Enviada em', value: formatDate(previewCampaign.sent_at) } : null,
                                            previewCampaign.tag_on_receive ? { label: 'Tag ao receber', value: previewCampaign.tag_on_receive, isTag: true } : null,
                                            previewCampaign.tag_on_click ? { label: 'Tag ao clicar', value: previewCampaign.tag_on_click, isTag: true } : null,
                                            previewCampaign.utm_source ? { label: 'UTM Source', value: previewCampaign.utm_source, isMono: true } : null,
                                            previewCampaign.utm_medium ? { label: 'UTM Medium', value: previewCampaign.utm_medium, isMono: true } : null,
                                            previewCampaign.utm_campaign ? { label: 'UTM Campaign', value: previewCampaign.utm_campaign, isMono: true } : null,
                                            previewCampaign.segment_tags?.length ? { label: 'Segmento tags', value: previewCampaign.segment_tags.join(', '), isTag: true } : null,
                                            previewCampaign.segment_countries?.length ? { label: 'Países', value: previewCampaign.segment_countries.join(', ') } : null,
                                        ].filter(Boolean).map((item: any) => (
                                            <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                                                <span className="text-xs text-gray-400">{item.label}</span>
                                                {item.isTag ? (
                                                    <Badge variant="secondary" className="text-[11px] rounded-md">{item.value}</Badge>
                                                ) : item.isMono ? (
                                                    <span className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-0.5 rounded">{item.value}</span>
                                                ) : (
                                                    <span className="text-xs font-medium text-gray-700">{item.value}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Footer actions */}
                                <div className="border-t bg-gray-50/50 px-5 py-3 flex gap-2">
                                    {previewCampaign.status === 'draft' && (
                                        <Button
                                            className="flex-1 rounded-xl bg-gray-900 hover:bg-gray-800 text-white"
                                            onClick={() => {
                                                if (confirm('Enviar esta campanha agora?')) {
                                                    resendMutation.mutate(previewCampaign.id);
                                                    setPreviewCampaign(null);
                                                }
                                            }}
                                        >
                                            <Send className="mr-2 h-4 w-4" /> Enviar agora
                                        </Button>
                                    )}
                                    {(previewCampaign.status === 'scheduled' || previewCampaign.status === 'sending') && (
                                        <Button variant="outline" className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50" onClick={() => { if (confirm('Cancelar campanha?')) { cancelMutation.mutate(previewCampaign.id); setPreviewCampaign(null); } }}>
                                            <Ban className="mr-2 h-4 w-4" /> Cancelar envio
                                        </Button>
                                    )}
                                    {previewCampaign.status === 'sent' && (
                                        <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { setResendConfirmCampaign(previewCampaign); setPreviewCampaign(null); }}>
                                            <RotateCcw className="mr-2 h-4 w-4" /> Reenviar
                                        </Button>
                                    )}
                                    {previewCampaign.status === 'cancelled' && (
                                        <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { setResendConfirmCampaign(previewCampaign); setPreviewCampaign(null); }}>
                                            <Send className="mr-2 h-4 w-4" /> Enviar novamente
                                        </Button>
                                    )}
                                    <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { navigate('/push/campaigns/new', { state: { duplicate: previewCampaign } }); setPreviewCampaign(null); }}>
                                        <Copy className="mr-2 h-4 w-4" /> Duplicar
                                    </Button>
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
}